import React, { createRef, useEffect, useState } from "react";

import Button from "../../components/Button";
import Collaborators from "../../components/Collaborators";
import Container from "../../components/Container";
import ContainerDivider from "../../components/ContainerDivider";
import EditButton from "../../components/EditButton/EditButton";
import FeaturedHeader from "../../components/FeaturedHeader";
import Footer from "../../components/Footer";
import Icon from "../../components/Icon";
import Navbar from "../../components/Navbar";
import Spacer from "../../components/Spacer";
import SubscriptionSection from "../../components/SubscriptionSection";
import TextField from "../../components/TextField";
import Textarea from "../../components/Textarea";
import addProfile from "../../helpers/addProfile";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import uploadCoverImage from "../../helpers/uploadCoverImage";
import uploadProfileImage from "../../helpers/uploadProfileImage";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useForm } from "react-hook-form";

export interface Profile {
  title: string;
  firstname: string;
  lastname: string;
  bio?: string;
  city: string;
  country: string;
  position: string;
  institute: string;
}

const EditProfile = () => {
  const user = useCurrentUser();

  // Handling form
  const {
    register,
    handleSubmit,
    errors,
    formState,
    setValue,
    trigger,
  } = useForm<Profile>({
    mode: "onChange",
  });
  const onSubmit = (data: Profile) => {
    addProfile(ID, data);
  };

  // Handling file upload
  const coverUploaderRef = createRef<HTMLInputElement>();
  const profileUploaderRef = createRef<HTMLInputElement>();
  const [profileImage, setProfileImage] = useState<File>();
  const [coverImage, setCoverImage] = useState<File>();

  // Get user info
  const { ID, firstname, image, cover, ticket } = user || {};

  // Initialize fields if user exists
  useEffect(() => {
    if (user) {
      if (user.title) setValue("title", user.title);
      if (user.firstname) setValue("firstname", user.firstname);
      if (user.lastname) setValue("lastname", user.lastname);
      if (user.position) setValue("position", user.position);
      if (user.bio) setValue("bio", user.bio);
      if (user.city) setValue("city", user.city);
      if (user.country) setValue("country", user.country);
      if (user.institute) setValue("institute", user.institute);

      trigger();
    }
  }, [user, setValue, trigger]);

  // Upload profile image
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(
    ""
  );
  useEffect(() => {
    if (profileImage) {
      const reader = new FileReader();
      reader.readAsDataURL(profileImage);
      reader.addEventListener("loadend", (e) => {
        setImagePreview(reader.result);
        uploadProfileImage(ID, profileImage);
      });
    }
  }, [ID, profileImage]);

  // Upload profile cover
  const [coverPreview, setCoverPreview] = useState<string | ArrayBuffer | null>(
    ""
  );
  useEffect(() => {
    if (coverImage) {
      const reader = new FileReader();
      reader.readAsDataURL(coverImage);
      reader.addEventListener("loadend", (e) => {
        setCoverPreview(reader.result);
        uploadCoverImage(ID, coverImage);
      });
    }
  }, [ID, coverImage]);

  return (
    <div className="ProfilePage">
      <Navbar />
      <FeaturedHeader image={coverPreview || cover}>
        <div className="lg:pb-20">
          <div className="text-3xl font-medium leading-tight">
            {firstname ? `Welcome back, ${firstname}!` : "Welcome!"}
          </div>
          <h1 className="mt-2 text-5xl font-bold leading-none">
            Edit Your Profile
          </h1>
        </div>
        <div className="absolute top-0 right-0 z-10 p-4">
          <EditButton
            onClick={() =>
              coverUploaderRef.current && coverUploaderRef.current.click()
            }
          />
        </div>
        <input
          type="file"
          className="hidden"
          ref={coverUploaderRef}
          accept="image/*"
          onChange={(e) => {
            if (e.target.files && e.target.files.length) {
              setCoverImage(e.target.files[0]);
            }
          }}
        />
      </FeaturedHeader>

      <div className="container relative px-4 py-12 mx-auto">
        <div className="absolute top-0 w-4/12 transform -translate-y-1/2 md:w-3/12 lg:w-2/12">
          <div
            className="relative bg-gray-200 bg-center bg-no-repeat bg-cover rounded-full shadow-xl"
            style={{
              backgroundImage: `url(${imagePreview || image})`,
              paddingTop: "100%",
            }}
          >
            {imagePreview || image ? null : (
              <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full">
                <Icon icon={faUser} />
              </div>
            )}
            <div className="absolute bottom-0 right-0 m-4">
              <EditButton
                onClick={() =>
                  profileUploaderRef.current &&
                  profileUploaderRef.current.click()
                }
              />
            </div>
            <input
              type="file"
              className="hidden"
              ref={profileUploaderRef}
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files.length) {
                  setProfileImage(e.target.files[0]);
                }
              }}
            />
          </div>
        </div>
      </div>

      <Container>
        <div className="flex flex-wrap w-full mt-8 space-y-8 lg:space-y-0 lg:space-x-8 lg:flex-no-wrap">
          <div className="w-full space-y-4 lg:w-8/12">
            <div className="text-lg font-medium text-aqua">Your Profile</div>
            <div className="space-y-4 text-base leading-loose">
              <form
                className="flex flex-wrap justify-start space-y-4"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="flex w-full space-x-4">
                  <TextField
                    type="text"
                    name="title"
                    placeholder="Mr. / Mrs. / Ms. / Dr."
                    register={register({
                      required: "Required",
                    })}
                    error={errors.title?.message}
                    required
                  />
                  <TextField
                    type="text"
                    name="firstname"
                    placeholder="First Name"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.firstname?.message}
                    required
                  />
                  <TextField
                    type="text"
                    name="lastname"
                    placeholder="Last Name"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.lastname?.message}
                    required
                  />
                </div>
                <div className="flex w-full space-x-4">
                  <TextField
                    type="text"
                    name="city"
                    placeholder="City"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.city?.message}
                    required
                  />
                  <TextField
                    type="text"
                    name="country"
                    placeholder="Country"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.country?.message}
                    required
                  />
                </div>
                <div className="flex w-full space-x-4">
                  <TextField
                    type="text"
                    name="position"
                    placeholder="Position"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.position?.message}
                    required
                  />
                  <TextField
                    type="text"
                    name="institute"
                    placeholder="Institute"
                    register={register({
                      required: "Required",
                    })}
                    error={errors.institute?.message}
                    required
                  />
                </div>
                <Textarea
                  name="bio"
                  placeholder="Write your bio..."
                  register={register}
                  error={errors.bio?.message}
                />
                <Button
                  type="submit"
                  disabled={!formState.isValid}
                  isLoading={formState.isSubmitting}
                >
                  Save Profile
                </Button>
              </form>
            </div>
          </div>
          {ticket ? (
            <div className="w-full space-y-4 lg:w-4/12">
              <div className="text-lg font-medium text-aqua">Ticket</div>
              <div className="space-y-2">
                <div className="text-sm text-gray-400">Order Number:</div>
                <div className="text-sm">{ticket.order}</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-gray-400">Status:</div>
                <div className="text-sm">{ticket.status}</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-gray-400">Type:</div>
                <div className="text-sm">{ticket.type}</div>
              </div>
            </div>
          ) : null}
        </div>
      </Container>

      <Spacer>
        <ContainerDivider />
        <Collaborators />
      </Spacer>

      <SubscriptionSection />
      <Footer />
    </div>
  );
};

export default EditProfile;
